import React from 'react';
import { wrapForcedLayout } from 'owa-performance';
import { subscribeToWindowEvent, unsubscribeToWindowEvent } from './subscribeToWindowEvent';

const USE_WINDOW_SIZE_REGISTRATION_KEY = Symbol('useWindowSize');
const windowSizeRegistration = new Map<
    Window,
    {
        width: number;
        height: number;
        callbacks: Map<
            any,
            (
                dimensions: Readonly<{
                    width: number;
                    height: number;
                }>
            ) => void
        >;
    }
>();

function subscribeToWindowSize(
    targetWindow: Window,
    refKey: any,
    scenario: string,
    callback: (
        dimensions: Readonly<{
            width: number;
            height: number;
        }>
    ) => void
) {
    if (!windowSizeRegistration.has(targetWindow)) {
        const resizeCallback = () => {
            /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
             * We need to read this for the purpose of this function, but this is done minimally to minimize performance issues.
             */
            const { innerWidth, innerHeight } = targetWindow;
            const dimensions = { width: innerWidth, height: innerHeight };
            const registration = windowSizeRegistration.get(targetWindow);
            if (registration) {
                registration.width = innerWidth;
                registration.height = innerHeight;
            }
            for (const cb of windowSizeRegistration.get(targetWindow)?.callbacks.values() ?? []) {
                cb(dimensions);
            }
        };
        subscribeToWindowEvent(
            targetWindow,
            'resize',
            USE_WINDOW_SIZE_REGISTRATION_KEY,
            scenario,
            resizeCallback
        );
        subscribeToWindowEvent(
            targetWindow,
            'orientationchange',
            USE_WINDOW_SIZE_REGISTRATION_KEY,
            scenario,
            resizeCallback
        );

        /* eslint-disable-next-line no-restricted-properties  -- (https://aka.ms/OWALintWiki)
         * We need to read this for the purpose of this function, but this is done minimally to minimize performance issues.
         */
        const { innerWidth: width, innerHeight: height } = targetWindow;
        windowSizeRegistration.set(targetWindow, {
            width,
            height,
            callbacks: new Map(),
        });
    }

    windowSizeRegistration.get(targetWindow)?.callbacks.set(refKey, callback);
}

function unsubscribeToWindowSize(targetWindow: Window, refKey: any) {
    windowSizeRegistration.get(targetWindow)?.callbacks.delete(refKey);
    if (windowSizeRegistration.get(targetWindow)?.callbacks.size === 0) {
        unsubscribeToWindowEvent(targetWindow, 'resize', USE_WINDOW_SIZE_REGISTRATION_KEY);
        unsubscribeToWindowEvent(
            targetWindow,
            'orientationchange',
            USE_WINDOW_SIZE_REGISTRATION_KEY
        );
        windowSizeRegistration.delete(targetWindow);
    }
}

export function useWindowSize(
    scenario: string,
    targetWindow?: Window
): {
    width: number;
    height: number;
} {
    const workWindow = targetWindow ?? window;

    const refKey = React.useRef();

    /* eslint-disable-next-line owa-custom-rules/require-typed-react-use-state -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE!, fix this issue or replace it with a justification
     *	> React.useState initialized with a function must specify a type parameter based on the return value. */
    const [windowDimensions, setWindowDimensions] = React.useState(() => {
        let workWindowInnerWidth = 0;
        let workWindowInnerHeight = 0;
        wrapForcedLayout('useWindowSize', () => {
            /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
             * This is baseline exception, if you edit this file you need to fix this exception.
             * We need to read this for the purpose of this function, but this is done at most one time per window instance.
             */
            workWindowInnerWidth = workWindow.innerWidth;
            /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
             * This is baseline exception, if you edit this file you need to fix this exception.
             * We need to read this for the purpose of this function, but this is done at most one time per window instance.
             */
            workWindowInnerHeight = workWindow.innerHeight;
        });

        return {
            width: windowSizeRegistration.get(workWindow)?.width ?? workWindowInnerWidth,
            height: windowSizeRegistration.get(workWindow)?.height ?? workWindowInnerHeight,
        };
    });

    React.useEffect(() => {
        /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, add a justification or fix the issue
         *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
        subscribeToWindowSize(workWindow, refKey, scenario, setWindowDimensions);

        return () => unsubscribeToWindowSize(workWindow, refKey);
    }, [workWindow]);

    return windowDimensions;
}
